import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { selectExistingRequest } from '@rsos/reduxDataStore';
import { getMostRecentLoc } from '@rsos/utils/getMostRecentLoc';
import { DEFAULT_FORM_VALUES } from '../constants';
import { formatAddressFromObject } from '../utils';
import { formatCoordinates, parseCoordinates } from '../validation';

const useInitialFormValues = () => {
  const selectedCallerID = useSelector(
    state => state.emergencyCalls.selectedCallerID,
  );
  const callQueue = useSelector(state => state.emergencyCalls.callQueue);
  const existingRequest = useSelector(selectExistingRequest);
  const formPageToShow = useSelector(
    state => state.airAmbulance.formPageToShow,
  );

  /**
   * @constant {object} callQueue -
   * Avoid adding {callQueue} to the dependency array.
   * Including {callQueue} would cause the address search location to reset
   * every time Hermes updates a new location.
   * We'll use the location that exists when the dispatcher initially opens the form.
   */

  return useMemo(() => {
    // If there's an existing request, populate the form with its data
    if (existingRequest?.body) {
      const { body } = existingRequest;
      // Handle different possible structures of the location data
      let latitude, longitude, pickupLocation;

      // First, check if coordinates are already formatted as a string
      if (body.pickup_coordinates || body.trip_data?.pickup_coordinates) {
        const coordString =
          body.pickup_coordinates || body.trip_data?.pickup_coordinates;
        const coords = parseCoordinates(coordString);
        if (coords) {
          latitude = coords.latitude;
          longitude = coords.longitude;
          pickupLocation =
            body.pickup_location || body.trip_data?.pickup_location || '';
        }
      }
      // Check if trip_data exists and has location
      else if (body.trip_data?.location) {
        const location = body.trip_data.location;
        latitude = parseFloat(location.latitude);
        longitude = parseFloat(location.longitude);
        pickupLocation = body.trip_data.pickup_location || '';
      }
      // Check if location exists directly on the body
      else if (body.location) {
        const location = body.location;
        latitude = parseFloat(location.latitude);
        longitude = parseFloat(location.longitude);
        pickupLocation = body.pickup_location || '';
      }
      // Check if lat/lng exist directly on the body
      else if (body.latitude && body.longitude) {
        latitude = parseFloat(body.latitude);
        longitude = parseFloat(body.longitude);
        pickupLocation = body.pickup_location || '';
      }

      // Format coordinates for the form
      const formattedCoordinates =
        !isNaN(latitude) && !isNaN(longitude)
          ? formatCoordinates(latitude, longitude)
          : '';
      // Get other form values from trip_data or directly from body
      const tripData = body.trip_data || body;

      // Map the existing request data to form fields
      return {
        ...DEFAULT_FORM_VALUES,
        pickup_coordinates: formattedCoordinates,
        pickup_location: pickupLocation,
        pickup_details: tripData.pickup_details || '',
        ground_contact: tripData.ground_contact || '',
        patient_info: tripData.patient_info || '',
        incident_info: tripData.incident_info || '',
        request_reason: tripData.trip_reason
          ? { value: tripData.trip_reason, label: tripData.trip_reason }
          : 'Select',
        multiple_units_requested: tripData.multiple_units_requested
          ? 'Yes'
          : 'No',
        launch_type: tripData.launch_type || 'Immediate',
        air_service_declined: tripData.weather_decline ? 'Yes' : 'No',
        air_service_responding: tripData.another_carrier ? 'Yes' : 'No',
      };
    }

    // If no existing request, use location from selected call if available
    if (!selectedCallerID || !callQueue || !callQueue[selectedCallerID]) {
      return DEFAULT_FORM_VALUES;
    }

    const selectedCall = callQueue[selectedCallerID];
    const latestLocation = getMostRecentLoc(selectedCall);

    if (!latestLocation) {
      return DEFAULT_FORM_VALUES;
    }

    const formattedAddress = latestLocation.addressObject
      ? formatAddressFromObject(latestLocation.addressObject)
      : latestLocation.rgAddress || '';

    return {
      ...DEFAULT_FORM_VALUES,
      pickup_coordinates: formatCoordinates(
        latestLocation.latitude,
        latestLocation.longitude,
      ),
      pickup_location: formattedAddress,
    };
  }, [selectedCallerID, existingRequest, formPageToShow]); // eslint-disable-line
};

export default useInitialFormValues;
